<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StarRating from './StarRating.vue';
import { formatCount, formatPrice, hasValue } from '../helpers';

const props = defineProps({
  product: { type: Object, default: () => ({}) },
  platform: { type: String, default: '' },
});

const { t, locale } = useI18n();

const articleLabel = computed(() => {
  if (props.platform === 'wildberries')
    return t('MULTICHAT.SOURCE.PRODUCT.NM_ID');
  return t('MULTICHAT.SOURCE.PRODUCT.SKU');
});

const article = computed(() => props.product.nm_id || props.product.sku);

const subtitle = computed(() =>
  [props.product.brand, props.product.category].filter(Boolean).join(' · ')
);
</script>

<template>
  <div class="flex gap-3 min-w-0">
    <div
      class="flex-shrink-0 size-14 rounded-lg bg-n-alpha-2 border border-n-weak grid place-items-center overflow-hidden"
    >
      <img
        v-if="product.image_url"
        :src="product.image_url"
        :alt="product.name"
        class="object-cover size-full"
      />
      <span v-else class="i-lucide-package size-6 text-n-slate-9" />
    </div>
    <div class="flex flex-col min-w-0 gap-1">
      <a
        v-if="product.url"
        :href="product.url"
        target="_blank"
        rel="noopener noreferrer"
        class="text-[13px] font-semibold leading-5 text-n-slate-12 hover:text-n-blue-11 hover:underline line-clamp-2"
      >
        {{ product.name }}
      </a>
      <span
        v-else
        class="text-[13px] font-semibold leading-5 text-n-slate-12 line-clamp-2"
      >
        {{ product.name }}
      </span>
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
      >
        <span
          v-if="hasValue(product.price)"
          class="font-semibold text-n-slate-12"
        >
          {{ formatPrice(product.price, locale) }}
        </span>
        <span
          v-if="hasValue(product.rating)"
          class="inline-flex items-center gap-1"
        >
          <StarRating :rating="Number(product.rating)" size="xs" show-value />
          <span v-if="hasValue(product.ratings_count)" class="text-n-slate-10">
            {{
              t('MULTICHAT.SOURCE.PRODUCT.RATINGS_COUNT', {
                count: formatCount(product.ratings_count, locale),
              })
            }}
          </span>
        </span>
        <span v-if="article" class="inline-flex items-center gap-1">
          <span class="text-n-slate-10">{{ articleLabel }}</span>
          <span class="font-mono text-n-slate-12">{{ article }}</span>
        </span>
        <span v-if="product.variant" class="inline-flex items-center gap-1">
          <span class="text-n-slate-10">
            {{ t('MULTICHAT.SOURCE.PRODUCT.VARIANT') }}
          </span>
          <span class="text-n-slate-12">{{ product.variant }}</span>
        </span>
      </div>
      <span v-if="subtitle" class="text-[11px] text-n-slate-10 truncate">
        {{ subtitle }}
      </span>
    </div>
  </div>
</template>
